// Jaguar-Owned — month + share-copy helpers shared between mobile and admin.
//
// `nominationTargetMonth` mirrors the server's nomination-window cutoff (the
// 5th of the month, evaluated in America/New_York — the zone the server's
// nomination trigger uses) so a member's client and the database agree on
// which month a nomination submitted right now lands in. `monthLabel` and
// `spotlightShareText` format the same 'YYYY-MM-01' month string for display
// and for the Business of the Month share copy. `classYearShort` lives here
// (not in mobile's businessCore.ts) because `spotlightShareText` needs it
// too — the admin composes the share sentence server-side from the same raw
// `class_year` column mobile reads, so both call sites format it identically.
// Plain TS, no build step (mirrors parsePostMedia in ./index.ts) so both
// mobile/src/features/business and admin/src/resources/business-spotlights
// import the exact same logic — the 5th-of-the-month cutoff and the share
// copy exist once.

/** `'2004'` → `"'04"`. Tolerates anything that isn't exactly 4 digits. */
export function classYearShort(year: string | null | undefined): string | null {
  if (typeof year !== 'string' || !/^\d{4}$/.test(year)) return null;
  return `'${year.slice(2)}`;
}

function isPositiveInt(n: number): boolean {
  return Number.isFinite(n) && n > 0;
}

/** `now`'s calendar date read from the machine's own local timezone. */
function localDateParts(now: Date): { year: number; month: number; day: number } {
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

/**
 * The calendar date `now` falls on, read in America/New_York regardless of the
 * device's own timezone — the zone the server's nomination trigger uses.
 *
 * Falls back to `now`'s LOCAL date parts if `Intl.DateTimeFormat` throws, or
 * if any of `formatToParts`'s year/month/day parts is missing or not a finite
 * positive integer. React Native's JS engine (Hermes) has shipped with
 * partial or missing ICU timezone data on some builds — a `'0000-00-00'`
 * silently parsed from a missing part would misfile a nomination by whole
 * YEARS (the previous version of this function did exactly that, via
 * `Number(undefined ?? '0')`), which is a far worse failure than the few
 * hours of drift a local-time guess can introduce.
 */
function nyDateParts(now: Date): { year: number; month: number; day: number } {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);
    const get = (type: string): number | null => {
      const raw = parts.find((p) => p.type === type)?.value;
      if (raw === undefined) return null;
      const n = Number(raw);
      return isPositiveInt(n) ? n : null;
    };
    const year = get('year');
    const month = get('month');
    const day = get('day');
    if (year === null || month === null || day === null) return localDateParts(now);
    return { year, month, day };
  } catch {
    return localDateParts(now);
  }
}

/**
 * `now`'s calendar month, read in America/New_York, as 'YYYY-MM-01' — the
 * ONE calendar `spotlightMonthEnded` (below) and `spotlightIsLive`
 * (mobile's businessCore.ts) both pin (fix round 2 ruling), so a spotlight's
 * liveness and its "has this month ended" verdict can never disagree with
 * each other, or with the server (`get_spotlight_report`'s SQL sets the same
 * zone) and the admin console. Reuses `nyDateParts` (and its local-time
 * fallback) rather than a second zone-reading implementation.
 */
export function nyMonthStart(now: Date = new Date()): string {
  const { year, month } = nyDateParts(now);
  return `${year}-${String(month).padStart(2, '0')}-01`;
}

function firstOfMonthAfter(year: number, month: number, monthsAhead: number): string {
  // month is 1-indexed; Date's month is 0-indexed, and rolls over years for us.
  const d = new Date(Date.UTC(year, month - 1 + monthsAhead, 1));
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
}

/**
 * Mirrors the server's nomination-month cutoff: on or before the 5th, a
 * nomination submitted now targets NEXT month; after the 5th, the month after
 * that (the following month's committee cycle has already started). Returns
 * 'YYYY-MM-01'.
 */
export function nominationTargetMonth(now: Date = new Date()): string {
  const { year, month, day } = nyDateParts(now);
  return firstOfMonthAfter(year, month, day <= 5 ? 1 : 2);
}

/**
 * True once `month` ('YYYY-MM-01') is entirely in the past, evaluated in
 * America/New_York — the same zone `get_spotlight_report`'s SQL function pins
 * (`set timezone to 'America/New_York'`,
 * `backend/supabase/migrations/20260923120200_jaguar_owned_spotlight.sql`),
 * so the client and the RPC agree on the exact instant a spotlight's report
 * becomes available. Built on `nyMonthStart` (fix round 2) — the SAME
 * calendar `spotlightIsLive` (mobile's businessCore.ts) now pins too, so the
 * two functions can never disagree about whether "right now" falls inside a
 * given spotlight's month. `'YYYY-MM-01'` strings compare correctly with
 * plain `>` (zero-padded, lexicographic order matches calendar order).
 *
 * A malformed `month` fails CLOSED to `false` ("not ended") — the mobile
 * caller (`MyBusinessScreen`'s "most recent ended spotlight" lookup) treats
 * that as "no report to show yet", never as a crash or a false report link.
 */
export function spotlightMonthEnded(month: string, now: Date = new Date()): boolean {
  const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(month);
  if (!m) return false;
  return nyMonthStart(now) > `${m[1]}-${m[2]}-01`;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** `'2026-10-01'` → `'October 2026'`. */
export function monthLabel(month: string): string {
  const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(month);
  if (!m) return month;
  const year = Number(m[1]);
  const idx = Number(m[2]) - 1;
  const name = MONTH_NAMES[idx] ?? m[2];
  return `${name} ${year}`;
}

/**
 * The Business of the Month share copy, pinned by admin D2:
 * `Our Business of the Month for October 2026 is Ledger & Grace Tax Services —
 * Andre Pierre '98. Find it in Jaguar-Owned on the MUAA Alumni app.`
 *
 * `classYear` is run through `classYearShort` — callers may pass the raw
 * 4-digit `class_year` column value (`'1998'` → `'98`) or an already-short
 * value (`classYearShort` returns null for anything that isn't exactly 4
 * digits, so a value like `"'98"` passes through unchanged rather than being
 * blanked). The class-year part is omitted entirely (no trailing space) when
 * `classYear` is null.
 */
export function spotlightShareText(args: {
  month: string;
  businessName: string;
  ownerName: string;
  classYear: string | null;
}): string {
  const { month, businessName, ownerName, classYear } = args;
  const shortYear = classYear === null ? null : (classYearShort(classYear) ?? classYear);
  const owner = shortYear ? `${ownerName} ${shortYear}` : ownerName;
  return `Our Business of the Month for ${monthLabel(month)} is ${businessName} — ${owner}. Find it in Jaguar-Owned on the MUAA Alumni app.`;
}
